import { z } from "zod";
import type { Alert } from "./alerts";
import type { OutcomeFold, OutcomeFoldLabel, ScoredFold } from "./qualifyCalibration";
import type { QualifyBand } from "./qualifyLead";
import { funnelBlockSchema, funnelDigestLines, type FunnelBlock } from "./scoreboardFunnel";
import { WEEK_MS } from "./week";
import { DAY_MS } from "./window";

/**
 * W7 SCOREBOARD (Training and Auto-Learn Master Spec B7 and B13, Agent Brains
 * 5.3 L4 "calibration audits on its own scores; unknown != assumed", CLAUDE.md
 * sections 4 and 11).
 *
 * The precision scoreboard as deterministic aggregates. Pure, no IO, no model:
 * a loader hands this file rows, this file counts them. The model never grades
 * a figure here (section 4), and the learner never edits what it is measured by
 * (section 3): nothing in this file is writable by an agent but the weekly
 * report itself.
 *
 * COUNTS, NEVER ROUNDED RATES (TA-251). A metric is `num` over `den`. A rate is
 * the reader's division, so no rounding can move a number a trend alarm reads.
 *
 * NOT MEASURED IS NOT ZERO. A metric below its sample floor, or with no source
 * at all, is `not_measured` with a reason, and the digest says so in words. A
 * measured zero is only ever a full sample with no event in it.
 *
 * A TREND ALARM needs the floor on BOTH sides of every comparison: it compares
 * three consecutive measured weeks, so a thin week breaks the chain instead of
 * being read as zero.
 */

/** Every metric the board reports, in the order the digest prints them. */
export const SCOREBOARD_METRICS = [
  "labelFalsePositive",
  "judgeOwnerAgreement",
  "verifierCatch",
  "inferenceAccuracy",
  "calibrationError",
  "repairRate",
  "aiTellRate",
  "bounceRate",
  "learnerSurvived30d",
  "learnerRealizedOverPredicted",
] as const;
export type ScoreboardMetric = (typeof SCOREBOARD_METRICS)[number];

/**
 * The fewest samples (the denominator) a metric needs before it is read.
 * AWAITING RATIFICATION: Kabir sets every one of these. 20 is the stratum bar
 * the learner design proposes for validation (open question 6), reused here so
 * the board does not speak on less evidence than the learner may act on; the
 * learner outcomes are fewer by nature, so they ask for 5.
 */
export const SCOREBOARD_FLOORS: Readonly<Record<ScoreboardMetric, number>> = Object.freeze({
  labelFalsePositive: 20, // AWAITING RATIFICATION
  judgeOwnerAgreement: 20, // AWAITING RATIFICATION
  verifierCatch: 20, // AWAITING RATIFICATION
  inferenceAccuracy: 20, // AWAITING RATIFICATION
  calibrationError: 20, // AWAITING RATIFICATION
  repairRate: 20, // AWAITING RATIFICATION
  aiTellRate: 20, // AWAITING RATIFICATION
  bounceRate: 20, // AWAITING RATIFICATION
  learnerSurvived30d: 5, // AWAITING RATIFICATION
  learnerRealizedOverPredicted: 5, // AWAITING RATIFICATION
});

/**
 * How far a rate must move, in percentage points, for a week to count as worse
 * than the one before it. AWAITING RATIFICATION. A margin, not a bar: crossing
 * it twice in a row raises an alert and nothing else, the alert changes no
 * threshold (the learner never edits its own objective, section 3).
 */
export const SCOREBOARD_TREND_MARGIN_PCT = 5; // AWAITING RATIFICATION

/** A learner change is judged after this long (the design's `learnerSurvived30d`). AWAITING RATIFICATION. */
export const LEARNER_SURVIVAL_DAYS = 30;

export type Measured =
  | { readonly status: "measured"; readonly num: number; readonly den: number }
  | { readonly status: "not_measured"; readonly reason: "below_floor" | "no_source"; readonly seen: number; readonly floor: number };

export interface Scoreboard {
  /** The Monday of the week reported, `YYYY-MM-DD` (week.ts `weekKey`). */
  readonly week: string;
  readonly metrics: Readonly<Record<ScoreboardMetric, Measured>>;
  /**
   * B1: the sales funnel (scoreboardFunnel.ts), added by the weekly task beside the ten metrics. Absent on a board
   * stored before it existed, and then simply not printed. It is not a trend metric: no alarm reads it.
   */
  readonly funnel?: FunnelBlock;
}

/** One composed draft: a model wrote it, so its first pass is evidence. Unknown is null, never false. */
export interface ScoreboardDraft {
  /** The FIRST attempt's verifier verdict (F-25). Null when it was not recorded. */
  readonly firstPassOk: boolean | null;
  /** The verifier rejected the final text too (status `blocked`). */
  readonly blocked: boolean;
  /** The linter's tell count on the stored body. Null when not recorded. */
  readonly aiTellCount: number | null;
}

export interface ScoreboardRows {
  readonly week: string;
  /** When the rows were read: reversals are observed up to here, and learner ships age against it. */
  readonly asOf: Date;
  /** Outcome ids of ghost labels the machine wrote in the week. */
  readonly ghostLabelsWritten: readonly string[];
  /** Outcome ids of ghost labels reversed, as observed by `asOf`. */
  readonly ghostLabelsReversed: readonly string[];
  readonly drafts: readonly ScoreboardDraft[];
  /** `bounce.recorded` rows in the week. */
  readonly bounces: number;
  /** One per owner-settled held memory row set: rows activated and rows superseded. */
  readonly memoryResolutions: readonly { readonly activated: number; readonly superseded: number }[];
  /** `lead.scored` rows that carried a band, and the outcome events (U3's folds). */
  readonly scored: readonly ScoredFold[];
  readonly outcomes: readonly OutcomeFold[];
  /** The voice judge's verdict against the owner's decision on the same draft. No source exists yet. */
  readonly judgePairs: readonly { readonly judgePass: boolean; readonly ownerApproved: boolean }[];
  /** Changes the learner shipped, and when (if ever) each was retracted or reverted. No source exists yet. */
  readonly learnerShips: readonly { readonly shippedAt: Date; readonly endedAt: Date | null }[];
  /** The learner's predicted lift against the realized one, per shipped change. No source exists yet. */
  readonly learnerEffects: readonly { readonly predicted: number; readonly realized: number }[];
}

function measure(metric: ScoreboardMetric, num: number, den: number, sourceless = false): Measured {
  const floor = SCOREBOARD_FLOORS[metric];
  if (den < floor) {
    return { status: "not_measured", reason: sourceless && den === 0 ? "no_source" : "below_floor", seen: den, floor };
  }
  return { status: "measured", num, den };
}

const RESOLVED: readonly OutcomeFoldLabel[] = ["sold", "booked", "deposit", "lost"];
const POSITIVE: readonly OutcomeFoldLabel[] = ["sold", "booked", "deposit"];
/** Higher first. `unknown` has no place in the order and is left out. */
const BAND_ORDER: readonly QualifyBand[] = ["priority", "standard", "low_signal"];

/**
 * The calibration error: of every pair of resolved leads from two different
 * bands where one ended well and one did not, how many had the lower band
 * doing the better. Ranks, not probabilities, because the score is a
 * normalized sum and not a probability (the `lead.scored` catalog note).
 * The band is the latest one scored BEFORE the contact's first resolved
 * outcome; a band written after the fact predicts nothing. Ghosts are not a
 * resolved outcome, exactly as `calibrateQualify` has it.
 */
function calibrationPairs(scored: readonly ScoredFold[], outcomes: readonly OutcomeFold[]): { discordant: number; comparable: number } {
  const withdrawn = new Set<string>();
  for (const o of outcomes) if (o.kind === "reversed" || o.kind === "ghost_reversed") withdrawn.add(o.outcomeId);
  const resolvedBy = new Map<string, { label: OutcomeFoldLabel; at: number }[]>();
  for (const o of outcomes) {
    if ((o.kind !== "recorded" && o.kind !== "auto_ghost") || o.label === null || withdrawn.has(o.outcomeId)) continue;
    if (!RESOLVED.includes(o.label)) continue;
    resolvedBy.set(o.contactId, [...(resolvedBy.get(o.contactId) ?? []), { label: o.label, at: o.occurredAt.getTime() }]);
  }
  const scoresBy = new Map<string, ScoredFold[]>();
  for (const s of scored) scoresBy.set(s.contactId, [...(scoresBy.get(s.contactId) ?? []), s]);

  const tally = new Map<QualifyBand, { pos: number; neg: number }>();
  for (const b of BAND_ORDER) tally.set(b, { pos: 0, neg: 0 });
  for (const [contactId, resolved] of resolvedBy) {
    const first = Math.min(...resolved.map((r) => r.at));
    let pick: ScoredFold | null = null;
    for (const s of scoresBy.get(contactId) ?? []) {
      if (s.occurredAt.getTime() > first) continue;
      if (pick === null || s.occurredAt.getTime() >= pick.occurredAt.getTime()) pick = s;
    }
    const cell = pick === null ? undefined : tally.get(pick.band);
    if (cell === undefined) continue;
    if (resolved.some((r) => POSITIVE.includes(r.label))) cell.pos += 1;
    else cell.neg += 1;
  }

  let discordant = 0;
  let comparable = 0;
  for (let i = 0; i < BAND_ORDER.length; i += 1) {
    for (let j = i + 1; j < BAND_ORDER.length; j += 1) {
      const hi = tally.get(BAND_ORDER[i]!)!;
      const lo = tally.get(BAND_ORDER[j]!)!;
      comparable += hi.pos * lo.neg + hi.neg * lo.pos;
      discordant += hi.neg * lo.pos;
    }
  }
  return { discordant, comparable };
}

export function buildScoreboard(r: ScoreboardRows): Scoreboard {
  const written = new Set(r.ghostLabelsWritten);
  const reversed = new Set(r.ghostLabelsReversed.filter((id) => written.has(id)));

  const known = r.drafts.filter((d) => d.firstPassOk !== null);
  const failedFirst = known.filter((d) => d.firstPassOk === false);
  const tellKnown = r.drafts.filter((d) => d.aiTellCount !== null);

  const activated = r.memoryResolutions.reduce((n, m) => n + m.activated, 0);
  const superseded = r.memoryResolutions.reduce((n, m) => n + m.superseded, 0);

  const cal = calibrationPairs(r.scored, r.outcomes);

  const asOf = r.asOf.getTime();
  const judgeable = r.learnerShips.filter((s) => s.shippedAt.getTime() + LEARNER_SURVIVAL_DAYS * DAY_MS <= asOf);
  const survived = judgeable.filter(
    (s) => s.endedAt === null || s.endedAt.getTime() >= s.shippedAt.getTime() + LEARNER_SURVIVAL_DAYS * DAY_MS,
  );

  return {
    week: r.week,
    metrics: {
      labelFalsePositive: measure("labelFalsePositive", reversed.size, written.size),
      judgeOwnerAgreement: measure(
        "judgeOwnerAgreement",
        r.judgePairs.filter((p) => p.judgePass === p.ownerApproved).length,
        r.judgePairs.length,
        true,
      ),
      verifierCatch: measure("verifierCatch", failedFirst.length, known.length),
      inferenceAccuracy: measure("inferenceAccuracy", activated, activated + superseded),
      calibrationError: measure("calibrationError", cal.discordant, cal.comparable),
      repairRate: measure("repairRate", failedFirst.filter((d) => !d.blocked).length, known.length),
      aiTellRate: measure("aiTellRate", tellKnown.filter((d) => (d.aiTellCount ?? 0) > 0).length, tellKnown.length),
      bounceRate: measure("bounceRate", r.bounces, r.drafts.length),
      learnerSurvived30d: measure("learnerSurvived30d", survived.length, judgeable.length, true),
      learnerRealizedOverPredicted: measure(
        "learnerRealizedOverPredicted",
        r.learnerEffects.filter((e) => e.realized >= e.predicted).length,
        r.learnerEffects.length,
        true,
      ),
    },
  };
}

// ---- the stored payload (one schema, used by the catalog and by the loader) ----

const ID = /^[A-Za-z0-9_.:-]{1,128}$/;

const measuredSchema = z.discriminatedUnion("status", [
  z.strictObject({ status: z.literal("measured"), num: z.number().int().nonnegative(), den: z.number().int().nonnegative() }),
  z.strictObject({
    status: z.literal("not_measured"),
    reason: z.enum(["below_floor", "no_source"]),
    seen: z.number().int().nonnegative(),
    floor: z.number().int().nonnegative(),
  }),
]);

/** The strict payload of `scoreboard.reported`: ids, enums and counts only, no customer word. */
export const scoreboardReportedSchema = z.strictObject({
  week: z.string().regex(ID),
  labelFalsePositive: measuredSchema,
  judgeOwnerAgreement: measuredSchema,
  verifierCatch: measuredSchema,
  inferenceAccuracy: measuredSchema,
  calibrationError: measuredSchema,
  repairRate: measuredSchema,
  aiTellRate: measuredSchema,
  bounceRate: measuredSchema,
  learnerSurvived30d: measuredSchema,
  learnerRealizedOverPredicted: measuredSchema,
  // Optional so a row written before the funnel block existed still parses.
  funnel: funnelBlockSchema.optional(),
});

export function scoreboardPayload(sb: Scoreboard): z.infer<typeof scoreboardReportedSchema> {
  return { week: sb.week, ...sb.metrics, ...(sb.funnel === undefined ? {} : { funnel: sb.funnel }) };
}

/** A stored payload back to a board, or null when it does not parse: never a guess. */
export function parseScoreboardPayload(payload: unknown): Scoreboard | null {
  const p = scoreboardReportedSchema.safeParse(payload);
  if (!p.success) return null;
  const { week, funnel, ...metrics } = p.data;
  return { week, metrics: metrics as Record<ScoreboardMetric, Measured>, ...(funnel === undefined ? {} : { funnel }) };
}

// ---- the digest template ----

const LABELS: Readonly<Record<ScoreboardMetric, string>> = {
  labelFalsePositive: "Machine labels later reversed",
  judgeOwnerAgreement: "Judge agreed with the owner",
  verifierCatch: "Drafts the verifier refused on the first pass",
  inferenceAccuracy: "Held memory rows the owner confirmed",
  calibrationError: "Lead bands that ranked a pair the wrong way",
  repairRate: "Drafts rescued by the corrective retry",
  aiTellRate: "Drafts with an AI tell",
  bounceRate: "Peer bounces per composed draft",
  learnerSurvived30d: "Learner changes standing after 30 days",
  learnerRealizedOverPredicted: "Learner changes that met their predicted lift",
};

/** The digest lines: counts as written, a missing figure in words, never a zero in its place. */
export function scoreboardDigestLines(sb: Scoreboard): string[] {
  const lines = [`Precision scoreboard, week of ${sb.week}`];
  for (const m of SCOREBOARD_METRICS) {
    const v = sb.metrics[m];
    if (v.status === "measured") lines.push(`${LABELS[m]}: ${v.num} of ${v.den}`);
    else if (v.reason === "no_source") lines.push(`${LABELS[m]}: not measured, no source yet`);
    else lines.push(`${LABELS[m]}: not measured, ${v.seen} seen and ${v.floor} needed`);
  }
  if (sb.funnel !== undefined) lines.push(...funnelDigestLines(sb.funnel));
  return lines;
}

// ---- the trend alarm ----

/** True when a RISING value is the worse direction. */
const HIGHER_IS_WORSE: Readonly<Record<ScoreboardMetric, boolean>> = {
  labelFalsePositive: true,
  judgeOwnerAgreement: false,
  // First pass refusals over composed drafts: more of them is the composer getting worse (F-25).
  verifierCatch: true,
  inferenceAccuracy: false,
  calibrationError: true,
  repairRate: true,
  aiTellRate: true,
  bounceRate: true,
  learnerSurvived30d: false,
  learnerRealizedOverPredicted: false,
};

/** Is `b` worse than `a` by at least the margin? Integer arithmetic over the counts, no division. */
function worseBy(metric: ScoreboardMetric, a: Extract<Measured, { status: "measured" }>, b: Extract<Measured, { status: "measured" }>): boolean {
  if (a.den === 0 || b.den === 0) return false;
  // rise = b.num/b.den - a.num/a.den, over the common denominator a.den * b.den.
  const rise = b.num * a.den - a.num * b.den;
  const signed = HIGHER_IS_WORSE[metric] ? rise : -rise;
  return 100 * signed >= SCOREBOARD_TREND_MARGIN_PCT * a.den * b.den;
}

/**
 * One alert per metric whose last three boards are consecutive weeks, all
 * measured, each worse than the one before by the margin. `history` is oldest
 * first and ends with the board just built.
 */
export function scoreboardTrendAlarms(history: readonly Scoreboard[]): Alert[] {
  if (history.length < 3) return [];
  const [w0, w1, w2] = history.slice(-3) as [Scoreboard, Scoreboard, Scoreboard];
  const step = (a: Scoreboard, b: Scoreboard): boolean => Date.parse(b.week) - Date.parse(a.week) === WEEK_MS;
  if (!step(w0, w1) || !step(w1, w2)) return [];

  const alarms: Alert[] = [];
  for (const m of SCOREBOARD_METRICS) {
    const a = w0.metrics[m];
    const b = w1.metrics[m];
    const c = w2.metrics[m];
    if (a.status !== "measured" || b.status !== "measured" || c.status !== "measured") continue;
    if (!worseBy(m, a, b) || !worseBy(m, b, c)) continue;
    alarms.push({
      kind: "scoreboard_trend",
      key: `scoreboard_trend:${m}:${w2.week}`,
      message:
        `The precision scoreboard metric ${m} got worse two weeks running by at least ${SCOREBOARD_TREND_MARGIN_PCT} points each week, ` +
        `${a.num} of ${a.den}, then ${b.num} of ${b.den}, then ${c.num} of ${c.den} (weeks of ${w0.week}, ${w1.week}, ${w2.week}). ` +
        "Nothing was changed; the scoreboard only reports.",
      at: new Date(Date.parse(w2.week) + WEEK_MS),
      detail: {
        metric: m,
        week: w2.week,
        marginPct: SCOREBOARD_TREND_MARGIN_PCT,
        series: [w0, w1, w2].map((w) => {
          const v = w.metrics[m] as Extract<Measured, { status: "measured" }>;
          return { week: w.week, num: v.num, den: v.den };
        }),
      },
    });
  }
  return alarms;
}
